import { type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { overlay, drawerContent, srOnly } from './drawer.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  anchor?: 'left' | 'right';
  width?: number | string;
  children: ReactNode;
  /**
   * Accessible name for the drawer dialog. Defaults to "Navigation drawer"
   * for backwards compatibility with the original API, but consumers using
   * Drawer for non-nav purposes (e.g. chapter ToC) should override it.
   */
  'aria-label'?: string;
}

export function Drawer({
  open,
  onClose,
  anchor = 'left',
  width = 320,
  children,
  'aria-label': ariaLabel = 'Navigation drawer',
}: DrawerProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlay} />
        <RadixDialog.Content
          className={drawerContent({ anchor })}
          style={{ width, maxWidth: '100%' }}
          aria-label={ariaLabel}
        >
          {/* Radix requires a Title node for screen-reader accessibility; an
              aria-label alone does not satisfy it. Render a visually-hidden one
              defaulting to the aria-label text. */}
          <RadixDialog.Title asChild>
            <span className={srOnly}>{ariaLabel}</span>
          </RadixDialog.Title>
          {/* Radix also warns without a Description (or aria-describedby) —
              same visually-hidden pattern StyledDialog uses. */}
          <RadixDialog.Description asChild>
            <span className={srOnly}>Drawer content</span>
          </RadixDialog.Description>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
